import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { credentialsAPI } from '../services/api.js';
import axios from 'axios'; 
import './CredentialsPage.css';

const API_BASE_URL = "https://quickpost.webzgrowth.in";

const CredentialsPage = () => {
  const { user, token } = useAuth();
  
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectingPlatform, setConnectingPlatform] = useState(null);

  // ✅ S3 base host from .env (React env must start with REACT_APP_)
  // Example:
  // REACT_APP_S3_IMAGES_BASE=https://bucket-datastore-mark1.s3.ap-south-1.amazonaws.com/Images
  const ICONS_BASE = (process.env.REACT_APP_S3_ICONS_BASE || "").replace(/\/$/, "");
  const IMAGES_BASE = (process.env.REACT_APP_S3_IMAGES_BASE || "").replace(/\/$/, "");


  // ✅ helper to build icons url
  const img = (fileName) => {
    const url = `${IMAGES_BASE}/${fileName}`;
    console.log("Generated URL:", url); // Debug log
    return url;
  };

  const ico = (fileName) => {
    const url = `${ICONS_BASE}/${fileName}`;
    console.log("Generated URL:", url); // Debug log
    return url;
  };

  // ✅ Debug: Log the base URL on component mount
  useEffect(() => {
    
    console.log("IMAGE_BASE:", ICONS_BASE);
    console.log("Sample URL:", ico("logo-header.png"));
  }, []);

  const platforms = [
    {
      id: 'facebook',
      name: 'Facebook with Instagram',
      icon: ico('fb.svg'),
      secondIcon: ico('insta.svg'),
      type: 'oauth_auto',
      fields: [],
      color: '#1877F2',
      displayName: 'Instagram With Facebook'
    },
    {
      id: 'youtube',
      name: 'YouTube',
      icon: ico('yt.svg'),
      color: '#ff0000',
      type: 'oauth_auto',
      fields: [],
      displayName: 'YouTube'
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: ico('link.svg'),
      fields: [],
      type: 'oauth_auto',
      color: '#0A66C2',
      displayName: 'LinkedIn'
    }
  ];

  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (user && token) {
      fetchCredentials();
    }
  }, [user, token]);

  const fetchCredentials = async () => {
    try {
      setLoading(true);
      const response = await credentialsAPI.getMyCredentials(token);
      if (response.success) {
        setCredentials(response.credentials);
      }
    } catch (err) {
      console.error('Error fetching credentials:', err);
    } finally {
      setLoading(false);
    }
  };

  const isConnected = (platformId) => {
    return credentials.some(cred => cred.platform === platformId && cred.is_connected);
  };

  const getCredential = (platformId) => {
    return credentials.find(cred => cred.platform === platformId);
  };

  const handleInputChange = (platformId, fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [platformId]: {
        ...prev[platformId],
        [fieldName]: value
      }
    }));
  };

  const handleFacebookAuthorizeAuto = async (platform) => {
    setConnectingPlatform(platform.id);

    try {
      console.log('🚀 Starting Facebook OAuth (includes Instagram)...');

      const response = await axios.post(
        `${API_BASE_URL}/api/oauth-auto/facebook/start`,
        { client_id: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.data.success) {
        alert('Failed to start Facebook authorization');
        setConnectingPlatform(null);
        return;
      }

      console.log('✅ OAuth URL received, opening popup...');

      const width = 600;
      const height = 700;
      const left = (window.screen.width / 2) - (width / 2);
      const top = (window.screen.height / 2) - (height / 2);

      const popup = window.open(
        response.data.authUrl,
        'Facebook Authorization',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        alert('Popup blocked! Please allow popups for this site.');
        setConnectingPlatform(null);
        return;
      }

      const messageHandler = (event) => {
        if (event.data.type === 'facebook-oauth-success') {
          console.log('✅ Facebook connected:', event.data.pageName);
          
          const message = event.data.instagramAccountId 
            ? `✅ Connected: ${event.data.pageName}\n✅ Instagram also connected!`
            : `✅ Connected: ${event.data.pageName}`;
          
          window.removeEventListener('message', messageHandler);
          setConnectingPlatform(null);
          
          alert(message);
          fetchCredentials();
          
        } else if (event.data.type === 'facebook-oauth-error') {
          console.error('❌ OAuth error:', event.data.error);
          
          window.removeEventListener('message', messageHandler);
          setConnectingPlatform(null);
          
          alert(`❌ Connection failed: ${event.data.error}`);
        }
      };

      window.addEventListener('message', messageHandler);

      const checkPopup = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopup);
          window.removeEventListener('message', messageHandler);
          setConnectingPlatform(null);
        }
      }, 1000);

    } catch (error) {
      console.error('❌ Error:', error);
      alert(`Failed to start authorization: ${error.message}`);
      setConnectingPlatform(null);
    }
  };

  const handleYouTubeAuthorizeAuto = async (platform) => {
    setConnectingPlatform(platform.id);

    try {
      console.log('🚀 Starting automatic YouTube OAuth...');

      const response = await axios.post(
        `${API_BASE_URL}/api/oauth-auto/youtube/start`,
        { client_id: null },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.data.success) {
        alert('Failed to start YouTube authorization');
        setConnectingPlatform(null);
        return;
      }

      console.log('✅ OAuth URL received, opening popup...');

      const width = 600;
      const height = 700;
      const left = (window.screen.width / 2) - (width / 2);
      const top = (window.screen.height / 2) - (height / 2);

      const popup = window.open(
        response.data.authUrl,
        'YouTube Authorization',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        alert('Popup blocked! Please allow popups for this site.');
        setConnectingPlatform(null);
        return;
      }

      const messageHandler = (event) => {
        if (event.data.type === 'youtube-oauth-success') {
          console.log('✅ YouTube connected:', event.data.channelTitle);
          
          window.removeEventListener('message', messageHandler);
          setConnectingPlatform(null);
          
          alert(`✅ Successfully connected: ${event.data.channelTitle}`);
          fetchCredentials();
          
        } else if (event.data.type === 'youtube-oauth-error') {
          console.error('❌ OAuth error:', event.data.error);
          
          window.removeEventListener('message', messageHandler);
          setConnectingPlatform(null);
          
          alert(`❌ Connection failed: ${event.data.error}`);
        }
      };

      window.addEventListener('message', messageHandler);

      const checkPopup = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopup);
          window.removeEventListener('message', messageHandler);
          setConnectingPlatform(null);
        }
      }, 1000);

    } catch (error) {
      console.error('❌ Error:', error);
      alert('Failed to start authorization: ' + error.message);
      setConnectingPlatform(null);
    }
  };

  const handleLinkedInConnect = async () => {
    setConnectingPlatform('linkedin');
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/oauth-auto/linkedin/start`,
        { client_id: null },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        const width = 600;
        const height = 700;
        const left = (window.screen.width / 2) - (width / 2);
        const top = (window.screen.height / 2) - (height / 2);

        const popup = window.open(
          response.data.authUrl,
          'LinkedIn OAuth',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        if (!popup) {
          alert('Popup blocked! Please allow popups for this site.');
          setConnectingPlatform(null);
          return;
        }

        const messageHandler = (event) => {
          if (event.data.type === 'linkedin-oauth-success') {
            console.log('✅ LinkedIn connected:', event.data);
            window.removeEventListener('message', messageHandler);
            setConnectingPlatform(null);
            alert('✅ Successfully connected to LinkedIn');
            fetchCredentials();
          } else if (event.data.type === 'linkedin-oauth-error') {
            console.error('❌ OAuth error:', event.data.error);
            window.removeEventListener('message', messageHandler);
            setConnectingPlatform(null);
            alert(`❌ Connection failed: ${event.data.error}`);
          }
        };

        window.addEventListener('message', messageHandler);

        const checkPopup = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkPopup);
            window.removeEventListener('message', messageHandler);
            setConnectingPlatform(null);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error connecting LinkedIn:', error);
      alert('Failed to connect LinkedIn');
      setConnectingPlatform(null);
    }
  };

  const handleConnect = async (platform) => {
    const platformData = formData[platform.id] || {};
    
    const missingFields = platform.fields.filter(field => !platformData[field.name]);
    if (missingFields.length > 0) {
      alert(`Please enter: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    setConnectingPlatform(platform.id);

    try {
      const credentialData = {
        platform: platform.id,
        account_name: `${user.name}'s ${platform.name}`,
        client_id: null,
        ...platformData
      };

      const verifyResult = await credentialsAPI.verifyCredential(credentialData, token);

      if (!verifyResult.success) {
        alert('❌ ' + verifyResult.message);
        setConnectingPlatform(null);
        return;
      }

      const addResult = await credentialsAPI.addCredential(credentialData, token);

      if (addResult.success) {
        alert('✅ ' + verifyResult.message);
        setFormData(prev => ({
          ...prev,
          [platform.id]: {}
        }));
        fetchCredentials();
      }

    } catch (err) {
      console.error('Connection error:', err);
      alert('Error: ' + (err.message || 'Failed to connect'));
    } finally {
      setConnectingPlatform(null);
    }
  };

  const handleDisconnect = async (platform) => {
    if (!window.confirm(`Disconnect ${platform.name}?`)) {
      return;
    }

    try {
      const credential = getCredential(platform.id);
      if (credential) {
        const result = await credentialsAPI.deleteCredential(credential.id, token);
        if (result.success) {
          alert(`${platform.name} disconnected successfully`);
          fetchCredentials();
        }
      }
    } catch (err) {
      console.error('Disconnect error:', err);
      alert('Error: ' + (err.message || 'Failed to disconnect'));
    }
  };

  if (loading) {
    return (
      <section className="connect-section">
        <div style={{ textAlign: 'center', color: '#333' }}>Loading...</div>
      </section>
    );
  }

  return (
    <section className="connect-section">
      <div className="connect-wrapper">
        <h2>Connect Your Social Media Accounts</h2>
        <p>Link your social media platforms to start scheduling posts</p>

        <div className="platform-grid">
          {platforms.map(platform => {
            const connected = isConnected(platform.id);
            const credential = getCredential(platform.id);

            return (
              <div 
                key={platform.id} 
                className={`platform-card ${connected ? 'connected' : ''}`}
              >
                <div className="platform-header">
                  {platform.secondIcon ? (
                    <div className="platform-icons">
                      <img src={platform.secondIcon} alt={`${platform.name} icon`} />
                      <img src={platform.icon} alt={`${platform.name} icon`} />
                    </div>
                  ) : (
                    <div className="platform-icons single">
                      <img src={platform.icon} alt={`${platform.name} icon`} />
                      <span>{platform.displayName}</span>
                    </div>
                  )}
                  {connected && <span className="connected-badge">✓ Connected</span>}
                </div>

                {platform.secondIcon && !connected && (
                  <h4>{platform.displayName}</h4>
                )}

                {connected ? (
                  <div className="connected-info">
                    <p className="account-name">{credential?.account_name}</p>
                    <p className="connected-date">
                      Connected on {new Date(credential?.created_at).toLocaleDateString()}
                    </p>
                    <button 
                      className="btn-disconnect"
                      onClick={() => handleDisconnect(platform)}
                    >
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <div className="connect-form">
                    {platform.fields.map(field => (
                      <input
                        key={field.name}
                        type={field.type || "text"}
                        placeholder={field.placeholder}
                        value={formData[platform.id]?.[field.name] || ''}
                        onChange={(e) => handleInputChange(platform.id, field.name, e.target.value)}
                        className="credential-input"
                      />
                    ))}
                    <button 
                      className="connect-btn"
                      onClick={() => {
                        if (platform.type === 'oauth_auto') {
                          if (platform.id === 'youtube') {
                            handleYouTubeAuthorizeAuto(platform);
                          } else if (platform.id === 'linkedin') {
                            handleLinkedInConnect(platform);
                          } else if (platform.id === 'facebook') {
                            handleFacebookAuthorizeAuto(platform);
                          }
                        } else {
                          handleConnect(platform);
                        }
                      }}
                      disabled={connectingPlatform === platform.id}
                    >
                      {connectingPlatform === platform.id ? 'Connecting...' : 'Connect'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CredentialsPage;
